'use es6';

import { useCallback } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Text } from 'foundations-components/components/Text';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { TrellisEPSaveButton } from 'editor-components/header/EPSaveButton';
import { SaveStates } from 'editor-components/EPConstants';
import { isLastSaveToday } from 'editor-components/utils/durationUtils';
import { View } from 'foundations-components/components/View';
import { getCanUseUniversalThemeEditorWithTheme } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { useDispatch, useSelector } from 'react-redux';
import { View as FoundationsView } from 'foundations-components/transitional/components/View';

/**
 * @param {Object} props
 * @param {boolean} props.allowedSave (required)
 * @param {string} [props.allowedSaveDisabledTooltip] (optional)
 * @param {string} props.contentTypeNoun (required)
 * @param {boolean} props.isDisabled (required)
 * @param {Function} props.onSaveClick (required)
 * @param {boolean} props.permanentlyDisabled (required)
 * @param {boolean} props.isUserJitad (required)
 * @param {Date} [props.lastSave] (optional)
 * @param {string} props.saveState (required)
 * @param {boolean} [props.shouldShowRevisions] (optional)
 * @param {Function} [props.onRevisionsClick] (optional)
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BaymaxSaveButton = ({
  allowedSave,
  allowedSaveDisabledTooltip,
  contentTypeNoun,
  isDisabled,
  onSaveClick,
  permanentlyDisabled,
  lastSave,
  saveState,
  shouldShowRevisions = true,
  onRevisionsClick
}) => {
  const dispatch = useDispatch();
  const canUseUniversalThemeEditorWithTheme = useSelector(getCanUseUniversalThemeEditorWithTheme);
  const handleOnRevisionsClick = useCallback(() => {
    if (onRevisionsClick) {
      onRevisionsClick();
    } else {
      dispatch(routeToPage('revisions'));
    }
  }, [dispatch, onRevisionsClick]);
  function renderTooltipTimestamp() {
    if (!lastSave) return null;
    const lastSaveToday = isLastSaveToday({
      lastSave,
      now: I18n.moment.portalTz().valueOf()
    });

    // include date if user has not saved today
    const format = lastSaveToday ? 'LT' : 'l LT';
    const lastSavedTimeDisplay = I18n.moment.portalTz(lastSave).format(format);
    return /*#__PURE__*/_jsx(FoundationsView, {
      marginBlock: "200",
      as: "div",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        "data-test-save-type": "lastSaved",
        "data-test-id": "last-saved-timestamp",
        message: "header.save.lastSaved",
        options: {
          time: lastSavedTimeDisplay
        }
      })
    });
  }
  function renderTooltipContent() {
    if (!allowedSave) {
      return /*#__PURE__*/_jsx(Text, {
        as: "div",
        typography: "body-100",
        marginBlock: "200",
        children: allowedSaveDisabledTooltip || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "components.header.saveButton.notAllowed",
          options: {
            noun: contentTypeNoun
          }
        })
      });
    } else if (permanentlyDisabled) {
      return /*#__PURE__*/_jsx("div", {
        "data-test-id": "save-disabled-drop",
        children: /*#__PURE__*/_jsx(Text, {
          as: "div",
          typography: "body-100",
          marginBlock: "200",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "components.header.saveDisabledDrop.save.message"
          })
        })
      });
    }
    return /*#__PURE__*/_jsxs(Text, {
      as: "div",
      typography: "body-100",
      children: [renderTooltipTimestamp(), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: canUseUniversalThemeEditorWithTheme ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "header.save.universalThemeEnabledAutosaveMessage"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "header.save.autosaveMessage"
        })
      }), shouldShowRevisions && /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: 'header.save.versionHistoryText',
          options: {
            versionHistoryLink: /*#__PURE__*/_jsx(Button, {
              use: "link",
              disabled: !allowedSave,
              onClick: handleOnRevisionsClick,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: 'header.save.versionHistoryLink'
              })
            })
          }
        })
      })]
    });
  }
  return /*#__PURE__*/_jsx(TrellisEPSaveButton, {
    isError: saveState === SaveStates.SAVE_FAILED,
    isSaved: saveState === SaveStates.SAVED_CHANGES,
    isSaving: saveState === SaveStates.SAVING_CHANGES,
    lastSave: lastSave,
    onClick: onSaveClick,
    renderTooltipContent: renderTooltipContent,
    saveState: saveState,
    disabled: !allowedSave || isDisabled || permanentlyDisabled,
    tooltipUse: "longform"
  });
};
export default BaymaxSaveButton;